import React, { useEffect, useRef } from 'react';

import {
  Box,
  Header,
  NbModal,
  NbModalProps,
  makeUclComponent,
} from '@rbilabs/universal-components';
import { setStatusBarBackgroundColor, setStatusBarHidden } from 'expo-status-bar';
import { isString, noop } from 'lodash-es';
import { Platform, StatusBar } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { IBaseProps } from '@rbi-ctg/frontend';
import CloseButton from 'components/close-button';
import { KeyboardAwareView } from 'components/ucl/keyboard-aware-view';
import useEffectOnce from 'hooks/use-effect-once';
import { useIsDesktopWebBreakpoint } from 'hooks/use-media-query';
import usePreventBodyScroll from 'hooks/use-prevent-body-scroll';
import { CustomEventNames, EventTypes, logRBIEvent } from 'state/amplitude';
import { spacing } from 'styles/constants';
import { hiddenAccessibilityPlatformProps } from 'utils/accessibility';
import logger from 'utils/logger';

import { FocusableContainer } from './focusable-container';
import theme from './theme';
import { IModalProps } from './types';

// Native iOS only — avoid isIOS(), which is also true on iOS Safari.
export const SAFE_AREA_Y_VALUE = Platform.OS === 'ios' ? true : 0;

export const ModalHeader = Header.withConfig({
  focusable: true,
  accessible: true,
  textAlign: 'center',
  lineHeight: '2xs',
  margin: 0,
});

const ModalContainer = NbModal.withConfig<{
  $backgroundColor?: NbModalProps['backgroundColor'];
  $backgroundImage?: NbModalProps['bgImage'];
}>(p => ({
  backgroundColor: p.$backgroundColor,
  bgImage: p.$backgroundImage ? p.$backgroundImage : null,
  _web: {
    backgroundColor: 'transparent',
  },
}));

export const CloseButtonWrapper = Box.withConfig(
  ({ isDismissButtonAbsolute }: { isDismissButtonAbsolute: boolean }) => {
    return {
      position: isDismissButtonAbsolute ? 'absolute' : 'unset',
      right: '$2',
      top: '$2',
      zIndex: 1,
      maxWidth: '$12',
      maxHeight: '$12',
      justifyContent: 'center',
      alignItems: 'center',
      _web: {
        borderRadius: '50%',
        safeAreaY: 0,
        marginLeft: 0,
      },
    };
  }
);

/** Only use this when you are presenting a modal INSIDE a page, not a route that is a modal.
 *  If a route use expo-router modal route + <CardModal/> */
const Modal = ({
  allowsDismiss = true,
  isDismissButtonAbsolute = true,
  invertedCloseButton = false,
  children,
  onDismiss = noop,
  closeButton: CloseButtonCmp = CloseButton,
  amplitudeEventData: inEventData,
  height,
  skipLoggingHeaderAndMessage = false,
  backgroundColor = theme.modalContentBackground,
  backgroundImage,
  header = null,
  footer = null,
  body = null,
  contentStyle,
  headerStyle,
  isErrorModal,
  hideStatusBar = false,
  scrollViewBounces = true,
  closeButtonStyle,
  isFullHeightContent,
  withScroll = true,
  headerTextIsBelowCloseButton = false,
  headerVariant = 'headerOne',
  isInline = false,
  additionalLoggingContext,
}: IModalProps) => {
  const amplitudeEventData = useRef(inEventData);
  usePreventBodyScroll();
  const isDesktopWebBreakpoint = useIsDesktopWebBreakpoint();
  const { top, bottom } = useSafeAreaInsets();
  const androidSafeAreaStyle =
    Platform.OS === 'android' && !isFullHeightContent
      ? {
          paddingTop: top || StatusBar.currentHeight || 0,
          paddingBottom: bottom,
        }
      : undefined;

  useEffectOnce(() => {
    const {
      modalHeader,
      modalMessage,
      modalAppearanceEventMessage,
      modalName,
    } = amplitudeEventData.current;
    const header = modalHeader;
    const message = modalMessage;

    const context = {
      ModalHeader: skipLoggingHeaderAndMessage ? '' : header,
      ModalMessage: skipLoggingHeaderAndMessage ? '' : isString(message) ? message : '',
      isErrorModal,
    };

    logRBIEvent({
      name: CustomEventNames.MODAL_APPEARANCE,
      type: EventTypes.Other,
      attributes: {
        ...context,
        ...(modalName && { name: modalName }),
        Message: modalAppearanceEventMessage,
        bodyMessage: context.ModalMessage,
        modalHeader: context.ModalHeader,
      },
    });

    if (isErrorModal) {
      logger.error({
        message: modalAppearanceEventMessage,
        ...context,
        ...additionalLoggingContext,
      });
    }
  });

  useEffect(() => {
    setStatusBarBackgroundColor(backgroundColor, false);
    setStatusBarHidden(hideStatusBar);

    return () => {
      setStatusBarHidden(false);
      setStatusBarBackgroundColor('transparent', false);
    };
  }, [backgroundColor, hideStatusBar]);

  const FinalModalContainer = isInline ? Box : ModalContainer;

  return (
    <FinalModalContainer
      isOpen
      size="full"
      onClose={onDismiss}
      $backgroundColor={backgroundColor}
      $backgroundImage={backgroundImage}
      height={height}
      safeAreaY={isFullHeightContent ? 0 : SAFE_AREA_Y_VALUE}
      style={androidSafeAreaStyle}
    >
      <NbModal.Content
        accessibilityRole="alert"
        accessibilityViewIsModal
        maxHeight="100%"
        backgroundColor={backgroundColor}
        safeAreaX={Platform.OS === 'ios' || 0}
        flex={1}
        style={{ ...contentStyle }}
      >
        <NbModal.Header
          padding={0}
          zIndex={1}
          borderBottomWidth={0}
          {...headerStyle}
          {...(isDesktopWebBreakpoint
            ? {
                _web: {
                  shadow: '1',
                  minHeight: '$14',
                  justifyContent: 'center',
                },
              }
            : {})}
        >
          {
            // prevent content overlay with the close button
            allowsDismiss && (
              <CloseButtonWrapper isDismissButtonAbsolute={isDismissButtonAbsolute}>
                <CloseButtonCmp
                  style={{ ...closeButtonStyle }}
                  onClick={onDismiss}
                  testID="modal-close-button"
                  inverted={invertedCloseButton}
                  {...hiddenAccessibilityPlatformProps}
                />
              </CloseButtonWrapper>
            )
          }
          {header &&
            (typeof header === 'string' ? (
              <ModalHeader
                variant={headerVariant}
                lineHeight="2xl"
                fontSize="lg"
                marginTop={!isDesktopWebBreakpoint && headerTextIsBelowCloseButton ? spacing.$3 : 0}
              >
                {header}
              </ModalHeader>
            ) : (
              header
            ))}
        </NbModal.Header>

        <KeyboardAwareView
          withScroll={withScroll}
          style={{ padding: 0, flex: 1 }}
          bounces={scrollViewBounces}
        >
          <FocusableContainer>{body || children}</FocusableContainer>
        </KeyboardAwareView>
        {footer && (
          <NbModal.Footer padding={0} backgroundColor="transparent">
            {footer}
          </NbModal.Footer>
        )}
      </NbModal.Content>
    </FinalModalContainer>
  );
};

export const ModalContent = makeUclComponent((props: IBaseProps) => {
  return (
    <Box paddingX="$4" paddingY="$6" {...props}>
      {props.children}
    </Box>
  );
});

export { ModalSize } from './types';
export { default as ModalHeading } from 'components/modal/modal-heading';
export default Modal;
